// https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md
/* eslint react/jsx-no-target-blank: 0 */
import React, { useEffect, useMemo, useState } from 'react'
import { Header, Icon, Loader, Dimmer, Button, Dropdown, Popup, Table } from 'semantic-ui-react'
import { useDispatch, useSelector } from 'react-redux'
import {
  cancelAProductRemoval,
  getNotices,
  initiateAProductRemoval,
  markNoticesReviewed,
  mergeProducts,
  setDeleteLoading,
} from '../../../redux/actions/products'
import { AppState } from '../../../redux/store'
import { Notice } from '../../../redux/types/products'
import { resetLookup, setIsLoading } from '../../../redux/actions/ui'
import { RemoveProductModal } from '../RemoveProductModal'
import { navigateToProductEdit } from '../../../utils/navigate'

interface Sort {
  by: string
  asc: boolean
}

const PAGE_SIZE = 10

const Notices = () => {
  const dispatch = useDispatch()
  const [removeConfirmation, setRemoveConfirmation] = useState<any>({})
  const [showReviewedNotices, setShowReviewedNotices] = useState<any>(false)
  const [brandsFilter, setBrandsFilter] = useState<any>([])
  const [kindFilter, setKindFilter] = useState<any>([])
  const [sort, setSort] = useState<Sort>({ by: 'created_at', asc: true })
  const [page, setPage] = useState<number>(0)

  const notices = useSelector((state: AppState) => state.products.notices)
  const noticesLoading = useSelector((state: AppState) => state.products.noticesLoading)
  const isLoading = useSelector((state: AppState) => state.ui.isLoading)

  useEffect(() => {
    // isLoading is set to false when the API response is received (middleware)
    dispatch(setIsLoading({ status: true }))
    dispatch(getNotices())
  }, [dispatch])

  useEffect(() => {
    resetPage()
  }, [brandsFilter, kindFilter, showReviewedNotices, sort])

  useEffect(() => {
    if (notices) {
      const result = {}
      Object.keys(notices).forEach((key) => {
        // @ts-ignore
        result[key] = false
      })
      setRemoveConfirmation(result)
    }
  }, [notices])

  const flipRemoveConfirmation = (product_uuid: string) => {
    const newRemoveConfirmation = {
      ...removeConfirmation,
      [product_uuid]: !removeConfirmation[product_uuid],
    }
    setRemoveConfirmation(newRemoveConfirmation)
  }

  const handleDeleteTrigger = (notice: Notice) => {
    // we only show the confirmation if the store count is nonzero
    if (notice.store_count && notice.store_count > 0) {
      flipRemoveConfirmation(notice.product_uuid)
    } else {
      deleteProduct({
        product_id: notice.product_id,
        removal_status: notice.removal_status,
        product_uuid: notice.product_uuid,
        kind: notice.kind,
      })
    }
  }

  const goToProduct = (id: number) => {
    dispatch(resetLookup())
    navigateToProductEdit({ productId: id })
  }

  const mergeTwoProducts = (notice: {
    active_product_uuid: string
    product_uuid: string
    kind: string
  }) => {
    if (notice.active_product_uuid && notice.product_uuid) {
      dispatch(
        mergeProducts({
          product_id: notice.active_product_uuid,
          duplicate_product_id: notice.product_uuid,
          notice: notice,
        })
      )
    }
  }

  type DeleteProductParams = Pick<Notice, 'product_id' | 'product_uuid' | 'kind' | 'removal_status'>

  const deleteProduct = (notice: DeleteProductParams) => {
    if (notice.removal_status === null) {
      dispatch(
        setDeleteLoading({
          productId: notice.product_id,
          deleteIsLoading: true,
          removalInitiated: true,
          notice,
        })
      )
      dispatch(
        initiateAProductRemoval({
          productId: notice.product_id,
          notice,
        })
      )
    } else {
      dispatch(
        cancelAProductRemoval({
          productId: notice.product_id,
        })
      )
    }
  }

  const getFilteredByReviewed = (noticesParam: Notice[], shouldShowReviewed: boolean) => {
    if (!noticesParam) {
      return []
    }
    if (shouldShowReviewed) {
      return noticesParam
    } else {
      return noticesParam.filter((notice: Notice) => !notice.reviewed)
    }
  }

  const getSorted = (noticesParam: Notice[], sortObj: Sort) => {
    if (!sortObj?.by) {
      return noticesParam
    }
    return noticesParam.sort((a, b) => {
      let first, second
      if (sortObj.by === 'created_at') {
        first = new Date(a.created_at)
        second = new Date(b.created_at)
      } else {
        first = a[sortObj.by]
        second = b[sortObj.by]
      }

      if (sortObj.asc) {
        if (typeof first === 'string') {
          return first.localeCompare(second)
        } else {
          return first - second
        }
      } else {
        if (typeof first === 'string') {
          return second.localeCompare(first)
        } else {
          return second - first
        }
      }
    })
  }

  const getDropdownOptions = (noticesParam: Notice[]) => {
    const allBrands = [...new Set(noticesParam.map((notice: Notice) => notice.brand))]
    return allBrands
      .map((brand: string) => ({
        key: brand ? brand : '',
        text: brand ? brand : '',
        value: brand ? brand : '',
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
  }

  const handleSortChange = (sortBy: string) => {
    if (sort && sort.by === sortBy) {
      setSort({
        by: sortBy,
        asc: !sort.asc,
      })
    } else {
      setSort({
        by: sortBy,
        asc: false,
      })
    }
  }

  const getSortIconName = (sortBy: string) => {
    if (sort.by === sortBy) {
      if (sort.asc) {
        return 'sort amount up'
      }
      return 'sort amount down'
    }
    return 'sort'
  }

  const sortIcon = (sortBy: string) => {
    return (
      <Icon name={getSortIconName(sortBy)} size="large" onClick={() => handleSortChange(sortBy)} />
    )
  }
  const noticesArray = useMemo(() => {
    return Object.values(notices)
  }, [notices])

  const brandDropDownOptions = useMemo(() => {
    return getDropdownOptions(getFilteredByReviewed(noticesArray, showReviewedNotices))
    // eslint-disable-next-line
  }, [noticesArray.length, showReviewedNotices])

  const getFiltered = (
    noticesParam: Notice[],
    brandsFilterArray: any,
    kindsToShow: any,
    shouldShowReviewed: boolean
  ) => {
    if (!noticesParam) {
      return []
    } else {
      return noticesParam.filter((notice: Notice) => {
        return (
          (brandsFilterArray.length === 0 || brandsFilterArray.includes(notice.brand)) &&
          (kindsToShow.length === 0 || kindsToShow.includes(notice.kind)) &&
          (shouldShowReviewed || !notice.reviewed)
        )
      })
    }
  }

  const toRenderNotices = useMemo(
    () => getSorted(getFiltered(noticesArray, brandsFilter, kindFilter, showReviewedNotices), sort),
    [noticesArray, brandsFilter, kindFilter, showReviewedNotices, sort]
  )

  const kindDropDownOptions = ['discontinued', 'duplicate', 'incorrect-brand'].map((x) => ({
    key: x,
    text: x,
    value: x,
  }))

  const canGoNextPage = () => (page + 1) * PAGE_SIZE < toRenderNotices.length
  const canGoPrevPage = () => page !== 0

  const incrPage = () => {
    if (canGoNextPage()) {
      setPage(page + 1)
    }
  }

  const decrPage = () => {
    if (canGoPrevPage()) {
      setPage(page - 1)
    }
  }

  const resetPage = () => setPage(0)

  const linkIcon = () => <Icon name="external alternate" className="notice-product-id-icon" />

  const iHeartJaneProductLink = (productId: number | null) => {
    if (productId === null) return undefined

    return `https://www.iheartjane.com/products/${productId}/content-tool`
  }

  const noticeRow = function ({ notice }: { notice: Notice }) {
    const productUuid = notice.product_uuid
    const deletionLoading = noticesLoading[productUuid].delete

    const markReviewedLoading = noticesLoading[productUuid].markReviewed
    const mergeLoading = noticesLoading[productUuid].merge
    return (
      <Table.Row>
        <Table.Cell>
          <span onClick={() => goToProduct(notice.product_id)} className="notice-product-id-link">
            {notice.product_id}
            {linkIcon()}
          </span>
        </Table.Cell>
        <Table.Cell>{notice.brand}</Table.Cell>
        <Table.Cell>
          <Popup
            content={notice.name}
            trigger={
              <span>
                <a
                  className="notice-product-id-link"
                  rel="noreferrer"
                  target="_blank"
                  href={iHeartJaneProductLink(notice.product_id)}
                >
                  {notice.name}
                  {linkIcon()}
                </a>
              </span>
            }
          />
        </Table.Cell>
        <Table.Cell>
          <span style={{ marginLeft: 'auto' }}>{notice.store_count}</span>
        </Table.Cell>
        <Table.Cell>{notice.kind}</Table.Cell>
        <Table.Cell>{new Date(notice.created_at).toLocaleString()}</Table.Cell>
        <Table.Cell>
          <Popup content={notice.comment} trigger={<span>{notice.comment}</span>} />
        </Table.Cell>
        <Table.Cell>
          <Popup
            content={notice.active_product_name}
            trigger={
              <span>
                <a
                  className="notice-product-id-link"
                  rel="noreferrer"
                  target="_blank"
                  href={iHeartJaneProductLink(notice.active_product_id)}
                >
                  {notice.active_product_name}
                  {notice.active_product_name && linkIcon()}
                </a>
              </span>
            }
          />
        </Table.Cell>
        <Table.Cell className="center-icon">
          <Icon
            loading={markReviewedLoading}
            name={markReviewedLoading ? 'spinner' : notice.reviewed ? 'checkmark' : 'x'}
            size="large"
          />
        </Table.Cell>
        <Table.Cell className="center-icon col-80">
          {!notice.product_removal_initiated && (
            <>
              <span className="cursor-pointer red-text" onClick={() => handleDeleteTrigger(notice)}>
                <Icon
                  loading={deletionLoading}
                  name={deletionLoading ? 'spinner' : 'trash'}
                  size="large"
                />
              </span>

              <RemoveProductModal
                open={removeConfirmation[notice.product_uuid]}
                setOpen={() => flipRemoveConfirmation(notice.product_uuid)}
                onConfirm={() => {
                  deleteProduct({
                    product_id: notice.product_id,
                    removal_status: notice.removal_status,
                    product_uuid: notice.product_uuid,
                    kind: notice.kind,
                  })
                }}
                removalStatus={notice.removal_status}
                productId={notice.product_id}
              />
            </>
          )}
        </Table.Cell>
        <Table.Cell className="center-icon col-80">
          {!notice.reviewed && notice.active_product_uuid && (
            <span
              className="cursor-pointer"
              onClick={() => {
                if (!mergeLoading && notice.active_product_uuid) {
                  const { active_product_uuid, kind, product_uuid } = notice
                  mergeTwoProducts({ active_product_uuid, kind, product_uuid })
                }
              }}
            >
              <Icon
                size="large"
                name={mergeLoading ? 'spinner' : 'code branch'}
                loading={mergeLoading}
              />
            </span>
          )}
        </Table.Cell>
        <Table.Cell className="center-icon col-80">
          {!notice.reviewed && (
            <Icon
              className="cursor-pointer"
              size="large"
              loading={markReviewedLoading}
              name={!markReviewedLoading ? 'zip' : 'spinner'}
              onClick={() => {
                // supplies an array of notices to mark as reviewed
                // for now, only one notice at a time.
                !markReviewedLoading &&
                  dispatch(
                    markNoticesReviewed({
                      notices: [
                        {
                          product_uuid: notice.product_uuid,
                          kind: notice.kind,
                        },
                      ],
                    })
                  )
              }}
            />
          )}
        </Table.Cell>
      </Table.Row>
    )
  }

  const NoticeRow = React.memo(
    noticeRow,
    (oldProps, newProps) => oldProps.notice === newProps.notice
  )

  const EmptyTable = () => (
    <Table.Row>
      <Table.Cell colSpan="12">
        <span>No new notices! 🎉</span>
      </Table.Cell>
    </Table.Row>
  )

  // keeps us from rendering too soon
  if (isLoading || !noticesArray) {
    return (
      <Dimmer active>
        <Loader>Loading Product Notices...</Loader>
      </Dimmer>
    )
  } else {
    return (
      <div className="ui basic padded segment">
        <Header as="h1">Product Notices</Header>
        <div className="flex-col space-between">
          <Button.Group>
            <Button
              positive={!showReviewedNotices}
              onClick={() => {
                setShowReviewedNotices(false)
                // reset on change
                setBrandsFilter([])
              }}
            >
              Unreviewed Notices
            </Button>
            <Button.Or />
            <Button
              positive={showReviewedNotices}
              onClick={() => {
                setShowReviewedNotices(true)
                // reset on change
                setBrandsFilter([])
              }}
            >
              All Notices
            </Button>
          </Button.Group>
          <div className="flex-row evenly-sized full-width">
            <Dropdown
              placeholder="Filter by brand.."
              multiple
              search
              selection
              options={brandDropDownOptions}
              onChange={(_event, data) => setBrandsFilter(data.value)}
              value={brandsFilter}
            />

            <Dropdown
              placeholder="Filter by notice kind.."
              multiple
              search
              selection
              options={kindDropDownOptions}
              onChange={(_event, data) => setKindFilter(data.value)}
              value={kindFilter}
            />
          </div>
        </div>
        <div className="notice-rows">
          <Table celled striped structured>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell rowSpan="2">
                  Product ID
                  {sortIcon('product_id')}
                </Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">Product Brand</Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">Product Name {sortIcon('name')}</Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">
                  Store Count {sortIcon('store_count')}
                </Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">Notice Kind</Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">
                  Notice Created At {sortIcon('created_at')}
                </Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">Comment</Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">Active Product Name</Table.HeaderCell>
                <Table.HeaderCell rowSpan="2">Reviewed?</Table.HeaderCell>
                <Table.HeaderCell colSpan="3" className="actions-header">
                  Actions <Icon name="mouse pointer" />
                </Table.HeaderCell>
              </Table.Row>
              <Table.Row className="actions-header">
                <Table.HeaderCell>Delete</Table.HeaderCell>
                <Table.HeaderCell>Merge</Table.HeaderCell>
                <Table.HeaderCell>Review</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {noticesArray.length === 0 ? (
                <EmptyTable />
              ) : (
                toRenderNotices
                  .slice(PAGE_SIZE * page, PAGE_SIZE * (page + 1))
                  .map((notice) => <NoticeRow key={notice.product_uuid} notice={notice} />)
              )}
            </Table.Body>
          </Table>
        </div>
        <div className="page-btns">
          <Button primary onClick={() => decrPage()} disabled={!canGoPrevPage()}>
            Previous
          </Button>
          <Button primary onClick={() => incrPage()} disabled={!canGoNextPage()}>
            Next
          </Button>
        </div>
      </div>
    )
  }
}

export default Notices
